/* eslint-disable import/prefer-default-export */
import chargerRebates from '../data/chargerRebates';
import VEHICLES from '../data/VEHICLES';

const typeConversions = {
  // Passenger vehicles:
  Sedan: 'Passenger',
  Crossover: 'Passenger',
  SUV: 'Passenger',
  Hatchback: 'Passenger',
  Coupe: 'Passenger',
  Minivan: 'Passenger',
  'Station Wagon': 'Passenger',

  // Non-Passenger vehicles
  transit_bus: 'Transit Bus',
  step_van: 'Step Van',
  pickup_truck: 'Pick-up Truck',
  school_bus: 'School Bus',
  cutaway_chassis: 'Cutaway Chassis',
  utility_truck: 'Utility Truck',
  box_truck: 'Box Truck',
  refuse_truck: 'Refuse Truck',
  trailer_truck: 'Trailer Truck',
  terminal_tractor: 'Terminal Tractor',
  motor_coach: 'Motor Coach',
  forklift: 'Forklift',
  cargo_van: 'Cargo Van',
  shuttle_bus: 'Shuttle Bus',
  specialty: 'Specialty',
  bev_drive_system: 'BEV Drive System',
  tru: 'TRU',
};

const passengerSubtypeConversions = {
  Sedan: 'Sedan',
  Crossover: 'SUV',
  SUV: 'SUV',
  Hatchback: 'Hatchback',
  Coupe: 'Coupe',
  Minivan: 'Minivan',
  'Station Wagon': 'Station Wagon',
  pickup_truck: 'Light Duty',
};

export const transformApiCharger = (charger) => {
  const chargerId = charger.charger_id || charger.id || charger.handle;
  const acChargeRate = charger.kw_ac ?? charger.ac_kw;
  const dcChargeRate = charger.kw_dc ?? charger.dc_kw;
  const rebateIndex = chargerRebates.findIndex(({ handle }) => handle === chargerId);

  return {
    fromApi: true,
    id: chargerId,
    make: charger.make,
    model: charger.model,
    chargerType: acChargeRate ? 'Level 2' : 'DCFC',
    chargeRate: acChargeRate || dcChargeRate,
    isEstimatedPrice: charger.is_estimated_price ?? charger.isEstimatedPrice,
    msrp: charger.price ?? charger.msrp,
    plugType: charger.plug_types || charger.plug_type || charger.plugType,
    rebate: chargerRebates[rebateIndex]?.rebate,
    imageUrl: charger.img || charger.image_url || charger.imageUrl,
    ports: charger.num_of_ports || charger.ports,
  };
};

/**
 *
 * @param {ApiVehicle} vehicle
 * @returns Vehicle
 */
export const transformApiVehicle = (vehicle) => {
  const vehicleImage = vehicle.thumbnail_url || vehicle.img_url || null;

  return {
    fromApi: true,
    id: vehicle.handle,
    availableNow: vehicle.availability === 'released',
    category: vehicle.form_factor === 'tru' ? 'TRU' : vehicle.is_offroad ? 'Off-Road' : 'On-Road',
    name: [vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(' '),
    type: typeConversion(vehicle),
    subtype: subtypeConversion(vehicle),
    formFactor: vehicle.form_factor,
    make: vehicle.make,
    year: vehicle.model_year,
    model: vehicle.model,
    modelYear: vehicle.model_year,
    duty: vehicle.duty,
    trim: vehicle.trim,
    imageUrl: vehicleImage,
    rangeInMiles: vehicle.total_range,
    rangeInHours: 0, // off-road vehicles are NOT included
    range: vehicle.total_range,
    electricRange: vehicle.electric_range,
    fuel: normalizeFuel(vehicle),
    liftCapacity: vehicle.lift_capacity || 0,
    batteryCapacityInKwh: vehicle.battery_capacity,
    weightClass: vehicle.weight_class,
    msrp: vehicle.msrp ?? vehicle.estimated_price ?? 0,
    milesPerGallon: vehicle.fossil_fuel_efficiency,
    msrpIsEstimated: vehicle.is_estimated_price ?? vehicle.msrp == null,
    maintenanceCostPerMile: maintenanceCostPerMile(vehicle),
    maintenanceCostPerHour: maintenanceCostPerHour(vehicle),
    efficiencyInMilesPerKwh: toPrecision(milesPerKwh(vehicle), 2) || null,
  };
};

/**
 *
 * @param {Archetype} archetype
 * @returns Vehicle
 */
export const transformArchetypeToFossilVehicle = (archetype) => ({
  fromApi: false,
  id: archetype.id,
  availableNow: true,
  category: archetype.category,
  name: archetype.name,
  type: archetype.type,
  subtype: archetype.subtype,
  formFactor: archetype.formFactor,
  make: null,
  year: null,
  model: null,
  modelYear: null,
  duty: archetype.duty,
  trim: null,
  imageUrl: archetype.imageUrl,
  rangeInMiles: archetype.rangeInMiles,
  rangeInHours: archetype.rangeInHours,
  range: archetype.rangeInMiles,
  fuel: archetype.fossilFuelType,
  liftCapacity: archetype.liftCapacity,
  batteryCapacityInKwh: archetype.batteryCapacityInKwh,
  weightClass: archetype.weightClass,
  msrp: archetype.fossilFuelMsrp,
  milesPerGallon: archetype.fossilMilesPerGallon,
  msrpIsEstimated: true,
  maintenanceCostPerMile: archetype.fossilMaintenanceCostPerMile,
  maintenanceCostPerHour: null,
  efficiencyInMilesPerKwh: null,
});

/**
 * In the new API:
 * - passenger rows still usually have type === "Passenger"
 * - commercial rows may have type === "box_truck" (etc), but original_values.type keeps "Commercial"
 */
function isPassengerVehicle(vehicle) {
  return (vehicle.original_values?.type || vehicle.type) === 'Passenger';
}

/**
 *
 * @param {ApiVehicle} vehicle
 * @returns {String}
 */
function typeConversion(vehicle) {
  return typeConversions[vehicle.form_factor] || vehicle.form_factor;
}

/**
 *
 * @param {ApiVehicle} vehicle
 * @returns {String}
 */
function subtypeConversion(vehicle) {
  if (isPassengerVehicle(vehicle)) {
    return passengerSubtypeConversions[vehicle.form_factor] || vehicle.subtype;
  }

  // Prefer legacy-cased subtype if present (helps match your VEHICLES archetypes)
  return vehicle.original_values?.subtype || vehicle.subtype;
}

function normalizeFuel(vehicle) {
  const raw = vehicle?.fuel;

  if (!raw) return null;

  const v = String(raw).trim().toLowerCase();

  // Electric / hybrid labels -> keep app's expected codes
  if (v === 'bev' || v === 'ev' || v === 'battery electric') return 'BEV';
  if (v === 'phev' || v === 'plug-in hybrid' || v === 'plugin hybrid') return 'PHEV';
  if (v === 'hev' || v === 'hybrid') return 'HEV';
  if (v === 'fcev' || v === 'hydrogen') return 'FCEV';

  // Fossil labels -> keep app's fossil strings
  if (v === 'gas' || v === 'gasoline') return 'gasoline';
  if (v === 'diesel') return 'diesel';

  // Fallback: if API provides fuel_types array
  const fuelTypes = vehicle?.fuel_types?.map((x) => String(x).toLowerCase()) || [];
  if (fuelTypes.includes('electricity')) return 'BEV';
  if (fuelTypes.includes('diesel')) return 'diesel';
  if (fuelTypes.includes('gasoline') || fuelTypes.includes('gas')) return 'gasoline';

  // Last resort: return original
  return raw;
}

/**
 *
 * @param {ApiVehicle} vehicle
 * @returns {Number}
 */
function maintenanceCostPerMile(vehicle) {
  if (vehicle.is_offroad) return 0;

  const unit = (vehicle.maintenance_cost_unit || '').toLowerCase();
  const isFossil = ['gasoline', 'gas', 'diesel'].includes((vehicle.fuel || '').toLowerCase());

  // Only treat it as per-mile if unit says mile (or unit is missing but value exists)
  if (vehicle.maintenance_cost && vehicle.maintenance_cost > 0 && (unit === 'mile' || !unit)) {
    return vehicle.maintenance_cost;
  }

  const archetype = VEHICLES.find(
    ({ type, subtype }) =>
      type === typeConversion(vehicle) && subtype === subtypeConversion(vehicle),
  );

  if (archetype) {
    return isFossil ? archetype.fossilMaintenanceCostPerMile : archetype.maintenanceCostPerMile;
  }

  return null;
}

function maintenanceCostPerHour(vehicle) {
  if (!vehicle.is_offroad) return 0;
  const unit = (vehicle.maintenance_cost_unit || '').toLowerCase();
  if (vehicle.maintenance_cost && vehicle.maintenance_cost > 0 && unit === 'hour') {
    return vehicle.maintenance_cost;
  }
  return 0;
}

/**
 *
 * @param {ApiVehicle} vehicle
 * returns {Number}
 */
function milesPerKwh(vehicle) {
  if (!vehicle.electric_efficiency) return;

  if (vehicle.electric_efficiency_unit === 'kwh per 100 miles') {
    return (1 / vehicle.electric_efficiency) * 100;
  } else if (vehicle.electric_efficiency_unit === 'kwh per mile') {
    return 1 / vehicle.electric_efficiency;
  }
}

function toPrecision(value, decimalPlaces) {
  const multiplier = Math.pow(10, decimalPlaces);
  return Math.round((value + Number.EPSILON) * multiplier) / multiplier;
}

export function transformLocation(location) {
  return {
    gasolinePrice: location.national_fuel_cost[0].gasoline.toFixed(2),
    dieselPrice: location.national_fuel_cost[0].diesel.toFixed(2),
  };
}
